import { useState, useEffect, useCallback } from "react";
import {
    FaHome,
    FaDollarSign,
    FaPercent,
    FaCalendarAlt,
    FaSpinner,
} from "react-icons/fa";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FaCalculator } from "react-icons/fa";

export default function MortageCalculator() {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        homePrice: "",
        downPayment: "",
        loanAmount: "",
        interestRate: "",
        loanTerm: "30",
        startDate: new Date().toISOString().split("T")[0],
        propertyTax: "",
        homeInsurance: "",
        pmi: "0.5",
        hoa: "",
    });
    const [results, setResults] = useState({
        monthlyPayment: "$0.00",
        principalInterest: "$0.00",
        taxes: "$0.00",
        insurance: "$0.00",
        pmi: "$0.00",
        hoa: "$0.00",
        totalInterest: "$0.00",
        totalCost: "$0.00",
        payoffDate: "",
        amortization: [],
    });

    // Calculate mortgage details
    const calculateMortage = useCallback((data = formData) => {
        setLoading(true);

        setTimeout(() => {
            try {
                const homePrice = parseFloat(data.homePrice) || 0;
                const downPayment = parseFloat(data.downPayment) || 0;
                const loanAmount =
                    parseFloat(data.loanAmount) || homePrice - downPayment;
                const annualRate = parseFloat(data.interestRate);
                const term = parseInt(data.loanTerm);
                const propertyTax = parseFloat(data.propertyTax) || 0;
                const homeInsurance = parseFloat(data.homeInsurance) || 0;
                const pmiRate = parseFloat(data.pmi) || 0;
                const hoa = parseFloat(data.hoa) || 0;

                if ((!homePrice && !loanAmount) || !annualRate || !term) {
                    alert(
                        "Please enter valid home price/loan amount, interest rate, and term"
                    );
                    return;
                }

                // Calculate monthly payments
                const monthlyRate = annualRate / 100 / 12;
                const payments = term * 12;
                const principalInterest =
                    (loanAmount * monthlyRate) /
                    (1 - Math.pow(1 + monthlyRate, -payments));

                // Calculate PMI (if down payment is less than 20%)
                const pmi =
                    downPayment < homePrice * 0.2
                        ? (loanAmount * (pmiRate / 100)) / 12
                        : 0;

                // Calculate monthly taxes and insurance
                const monthlyTax = propertyTax / 12;
                const monthlyInsurance = homeInsurance / 12;

                // Calculate total monthly payment
                const monthlyPayment =
                    principalInterest +
                    monthlyTax +
                    monthlyInsurance +
                    pmi +
                    hoa;

                // Calculate total interest and cost
                const totalInterest = principalInterest * payments - loanAmount;
                const totalCost =
                    loanAmount +
                    totalInterest +
                    monthlyTax * payments +
                    monthlyInsurance * payments +
                    pmi * payments +
                    hoa * payments;

                // Calculate payoff date
                const startDate = new Date(data.startDate);
                startDate.setMonth(startDate.getMonth() + payments);
                const payoffDate = formatDate(startDate);

                // Generate amortization schedule
                let balance = loanAmount;
                const amortization = [];

                for (let i = 1; i <= payments; i++) {
                    const interest = balance * monthlyRate;
                    const principal = principalInterest - interest;
                    balance -= principal;

                    amortization.push({
                        period: i,
                        payment: principalInterest,
                        principal,
                        interest,
                        balance,
                        date: new Date(
                            startDate.getFullYear(),
                            startDate.getMonth() + i - 1,
                            startDate.getDate()
                        ),
                    });

                    if (balance <= 0) break;
                }

                // Update results
                setResults({
                    monthlyPayment: formatCurrency(monthlyPayment),
                    principalInterest: formatCurrency(principalInterest),
                    taxes: formatCurrency(monthlyTax),
                    insurance: formatCurrency(monthlyInsurance),
                    pmi: formatCurrency(pmi),
                    hoa: formatCurrency(hoa),
                    totalInterest: formatCurrency(totalInterest),
                    totalCost: formatCurrency(totalCost),
                    payoffDate,
                    amortization,
                });

                // Save to localStorage
                localStorage.setItem(
                    "mortgageData",
                    JSON.stringify({
                        ...data,
                        startDate: data.startDate,
                    })
                );
            } catch (error) {
                console.error("Calculation error:", error);
                alert(
                    "An error occurred during calculation. Please check your inputs."
                );
            } finally {
                setLoading(false);
            }
        }, 300);
    }, [formData]);

    // Load saved data from localStorage
    useEffect(() => {
        const saved = JSON.parse(localStorage.getItem("mortageData"));
        if (saved) {
            setFormData((prev) => ({
                ...prev,
                ...saved,
                startDate:
                    saved.startDate || new Date().toISOString().split("T")[0],
            }));

            if (
                (saved.homePrice || saved.loanAmount) &&
                saved.interestRate &&
                saved.loanTerm
            ) {
                calculateMortage(saved);
            }
        }
    }, [calculateMortage]);

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(amount);
    };

    // Format date
    const formatDate = (dateString) => {
        const options = { year: "numeric", month: "long", day: "numeric" };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Calculate loan amount if home price or down payment changes
    useEffect(() => {
        if (formData.homePrice && formData.downPayment) {
            const loanAmount =
                parseFloat(formData.homePrice) -
                parseFloat(formData.downPayment);
            setFormData((prev) => ({
                ...prev,
                loanAmount: loanAmount > 0 ? loanAmount.toString() : "0",
            }));
        }
    }, [formData.homePrice, formData.downPayment]);

    // Handle input changes
    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    return (
        <>
            <HeaderSection toolId="mortageCalculator" />
            <div className="max-w-4xl mx-auto p-4  lg:max-w-full lg:px-4">
                <main className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white  dark:bg-gray-800 dark:shadow-gray-900/10 dark:border-gray-700">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon icon={faHome} className="mr-3" />
                            Mortage Calculator
                        </h1>
                        <p className="opacity-90">
                            Calculate your mortage payments with comprehensive
                            breakdown
                        </p>
                    </div>
                    <article className="container mx-auto px-4 py-8 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
                        <section className="max-w-4xl mx-auto rounded-2xl bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900/20 overflow-hidden border border-gray-200 dark:border-gray-700 transition-all duration-300">
                            <div className="relative p-6 md:p-8">
                                {/* Header */}
                                <header className="text-center mb-8">
                                    <h1 className="text-3xl font-bold mb-2 inline-flex items-center text-green-600 dark:text-green-500">
                                        <FaHome
                                            className="mr-3"
                                            aria-hidden="true"
                                        />
                                        Mortgage Calculator
                                    </h1>
                                    <div
                                        className="h-1 w-20 mx-auto bg-green-400 dark:bg-green-600 rounded-full"
                                        aria-hidden="true"
                                    ></div>
                                </header>

                                {/* Calculator Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* Input Section */}
                                    <section className="space-y-6">
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                calculateMortage();
                                            }}
                                        >
                                            <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Home Price */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="homePrice"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Home Price
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaDollarSign aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="number"
                                                            id="homePrice"
                                                            value={
                                                                formData.homePrice
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            placeholder="e.g. 350000"
                                                            min="0"
                                                            step="1000"
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Down Payment */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="downPayment"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Down Payment
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaDollarSign aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="number"
                                                            id="downPayment"
                                                            value={
                                                                formData.downPayment
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            placeholder="e.g. 70000"
                                                            min="0"
                                                            step="1000"
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Loan Amount */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="loanAmount"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Loan Amount
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaDollarSign aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="number"
                                                            id="loanAmount"
                                                            value={
                                                                formData.loanAmount
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            placeholder="Calculated automatically"
                                                            min="0"
                                                            step="1000"
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>

                                                {/* Interest Rate */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="interestRate"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Interest Rate (%)
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaPercent aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="number"
                                                            id="interestRate"
                                                            value={
                                                                formData.interestRate
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            placeholder="e.g. 3.5"
                                                            min="0"
                                                            max="20"
                                                            step="0.01"
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Loan Term */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="loanTerm"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Loan Term (Years)
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaCalendarAlt aria-hidden="true" />
                                                        </div>
                                                        <select
                                                            id="loanTerm"
                                                            value={
                                                                formData.loanTerm
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 transition-colors duration-300"
                                                        >
                                                            <option value="10">
                                                                10 years
                                                            </option>
                                                            <option value="15">
                                                                15 years
                                                            </option>
                                                            <option value="20">
                                                                20 years
                                                            </option>
                                                            <option value="30">
                                                                30 years
                                                            </option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Start Date */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="startDate"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Start Date
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaCalendarAlt aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="date"
                                                            id="startDate"
                                                            value={
                                                                formData.startDate
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
                                                        />
                                                    </div>
                                                </div>
                                            </fieldset>

                                            {/* Additional Costs */}
                                            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Property Tax */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="propertyTax"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Annual Property Tax
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaDollarSign aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="number"
                                                            id="propertyTax"
                                                            value={
                                                                formData.propertyTax
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            placeholder="e.g. 3000"
                                                            min="0"
                                                            step="100"
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Home Insurance */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="homeInsurance"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Annual Home Insurance
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaDollarSign aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="number"
                                                            id="homeInsurance"
                                                            value={
                                                                formData.homeInsurance
                                                            }
                                                            onChange={
                                                                handleChange
                                                            }
                                                            placeholder="e.g. 1200"
                                                            min="0"
                                                            step="100"
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
                                                        />
                                                    </div>
                                                </div>

                                                {/* PMI */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="pmi"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        PMI Rate (%)
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaPercent aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="number"
                                                            id="pmi"
                                                            value={formData.pmi}
                                                            onChange={
                                                                handleChange
                                                            }
                                                            placeholder="e.g. 0.5"
                                                            min="0"
                                                            max="2"
                                                            step="0.1"
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
                                                        />
                                                    </div>
                                                </div>

                                                {/* HOA Fees */}
                                                <div className="space-y-2">
                                                    <label
                                                        htmlFor="hoa"
                                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                                    >
                                                        Monthly HOA Fees
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-green-600 dark:text-green-500">
                                                            <FaDollarSign aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="number"
                                                            id="hoa"
                                                            value={formData.hoa}
                                                            onChange={
                                                                handleChange
                                                            }
                                                            placeholder="e.g. 100"
                                                            min="0"
                                                            step="10"
                                                            className="w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-300"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Calculate Button */}
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className="w-full mt-6 py-3 px-6 rounded-lg font-semibold text-white bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 shadow-md transition-all duration-300 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                                            >
                                                {loading ? (
                                                    <>
                                                        <FaSpinner
                                                            className="animate-spin mr-3"
                                                            aria-hidden="true"
                                                        />
                                                        <span>
                                                            Calculating...
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaCalculator
                                                            className="mr-3"
                                                            aria-hidden="true"
                                                        />
                                                        <span>Calculate</span>
                                                    </>
                                                )}
                                            </button>
                                        </form>
                                    </section>

                                    {/* Results Preview */}
                                    <aside className="p-6 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                                        <h2 className="text-xl font-semibold mb-6 text-gray-800 dark:text-gray-200">
                                            Mortgage Summary
                                        </h2>

                                        {/* Monthly Payment Breakdown */}
                                        <div className="mb-6">
                                            <h3 className="text-lg font-medium mb-3 text-gray-700 dark:text-gray-300">
                                                Monthly Payment
                                            </h3>
                                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 transition-colors duration-300">
                                                <div className="text-3xl font-bold text-green-600 dark:text-green-500 mb-4">
                                                    {results.monthlyPayment}
                                                </div>

                                                <div className="space-y-2">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600 dark:text-gray-400">
                                                            Principal & Interest
                                                        </span>
                                                        <span className="font-medium dark:text-gray-200">
                                                            {
                                                                results.principalInterest
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600 dark:text-gray-400">
                                                            Property Tax
                                                        </span>
                                                        <span className="font-medium dark:text-gray-200">
                                                            {results.taxes}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600 dark:text-gray-400">
                                                            Home Insurance
                                                        </span>
                                                        <span className="font-medium dark:text-gray-200">
                                                            {results.insurance}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600 dark:text-gray-400">
                                                            PMI
                                                        </span>
                                                        <span className="font-medium dark:text-gray-200">
                                                            {results.pmi}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600 dark:text-gray-400">
                                                            HOA Fees
                                                        </span>
                                                        <span className="font-medium dark:text-gray-200">
                                                            {results.hoa}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Totals */}
                                        <div className="grid grid-cols-2 gap-4 mb-6">
                                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 transition-colors duration-300">
                                                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                    Total Interest
                                                </h4>
                                                <div className="text-xl font-bold text-green-600 dark:text-green-500">
                                                    {results.totalInterest}
                                                </div>
                                            </div>
                                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 transition-colors duration-300">
                                                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                    Total Cost
                                                </h4>
                                                <div className="text-xl font-bold text-green-600 dark:text-green-500">
                                                    {results.totalCost}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Payoff Date */}
                                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 transition-colors duration-300">
                                            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                Payoff Date
                                            </h4>
                                            <div className="text-lg font-bold text-green-600 dark:text-green-500 flex items-center">
                                                <FaCalendarAlt className="mr-2" />
                                                {results.payoffDate || "N/A"}
                                            </div>
                                        </div>
                                    </aside>
                                </div>

                                {/* Amortization Table */}
                                {results.amortization.length > 0 && (
                                    <section
                                        className="mt-12"
                                        aria-labelledby="amortization-heading"
                                    >
                                        <h2
                                            id="amortization-heading"
                                            className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-200"
                                        >
                                            Amortization Schedule
                                        </h2>

                                        <div className="rounded-lg overflow-hidden bg-white dark:bg-gray-800 shadow-sm transition-colors duration-300">
                                            <div className="overflow-x-auto">
                                                <table className="w-full">
                                                    <caption className="sr-only">
                                                        Mortgage payment
                                                        breakdown by period
                                                    </caption>
                                                    <thead className="bg-gray-100 dark:bg-gray-700">
                                                        <tr>
                                                            <th
                                                                scope="col"
                                                                className="py-3 px-4 text-left text-green-600 dark:text-green-500"
                                                            >
                                                                #
                                                            </th>
                                                            <th
                                                                scope="col"
                                                                className="py-3 px-4 text-left text-green-600 dark:text-green-500"
                                                            >
                                                                Date
                                                            </th>
                                                            <th
                                                                scope="col"
                                                                className="py-3 px-4 text-left text-green-600 dark:text-green-500"
                                                            >
                                                                Payment
                                                            </th>
                                                            <th
                                                                scope="col"
                                                                className="py-3 px-4 text-left text-green-600 dark:text-green-500"
                                                            >
                                                                Principal
                                                            </th>
                                                            <th
                                                                scope="col"
                                                                className="py-3 px-4 text-left text-green-600 dark:text-green-500"
                                                            >
                                                                Interest
                                                            </th>
                                                            <th
                                                                scope="col"
                                                                className="py-3 px-4 text-left text-green-600 dark:text-green-500"
                                                            >
                                                                Balance
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {results.amortization
                                                            .slice(0, 12)
                                                            .map(
                                                                (
                                                                    row,
                                                                    index
                                                                ) => (
                                                                    <tr
                                                                        key={
                                                                            index
                                                                        }
                                                                        className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-300"
                                                                    >
                                                                        <th
                                                                            scope="row"
                                                                            className="py-3 px-4 dark:text-gray-300"
                                                                        >
                                                                            {
                                                                                row.period
                                                                            }
                                                                        </th>
                                                                        <td className="py-3 px-4 dark:text-gray-300">
                                                                            {row.date.toLocaleDateString()}
                                                                        </td>
                                                                        <td className="py-3 px-4 dark:text-gray-300">
                                                                            {formatCurrency(
                                                                                row.payment
                                                                            )}
                                                                        </td>
                                                                        <td className="py-3 px-4 dark:text-gray-300">
                                                                            {formatCurrency(
                                                                                row.principal
                                                                            )}
                                                                        </td>
                                                                        <td className="py-3 px-4 dark:text-gray-300">
                                                                            {formatCurrency(
                                                                                row.interest
                                                                            )}
                                                                        </td>
                                                                        <td className="py-3 px-4 dark:text-gray-300">
                                                                            {formatCurrency(
                                                                                row.balance
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {results.amortization.length >
                                                12 && (
                                                <div className="text-center py-4 text-sm text-gray-600 dark:text-gray-400">
                                                    Showing first 12 months of{" "}
                                                    {
                                                        results.amortization
                                                            .length
                                                    }{" "}
                                                    payments
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}
                            </div>
                        </section>
                    </article>
                </main>
            </div>
            <BottomSection toolId="mortageCalculator" />
        </>
    );
}
